/**
 * The length model for the variable-length ladder (spec §10.1).
 *
 * Deliberately data-free: this module is imported by core, by the worker,
 * and by the web's main bundle, so it must never pull a word list in with
 * it. Nothing here knows what the words *are* — only how long they may be,
 * how many rows that buys, and what a solve is worth.
 */

/** Playable answer lengths (spec §10.1). */
export const SUPPORTED_LENGTHS = [4, 5, 6, 7] as const;

export type SupportedLength = (typeof SUPPORTED_LENGTHS)[number];

export function isSupportedLength(value: number): value is SupportedLength {
  return (SUPPORTED_LENGTHS as readonly number[]).includes(value);
}

/**
 * Guess rows for a game: `length + 1` (4→5 … 7→8), replacing the fixed
 * `MAX_GUESSES` (spec §10.1).
 *
 * Takes the answer itself or its length because `answer.length` is the
 * only place a game's length is ever stored — `GameState` gains no field.
 * Throws rather than guessing: an unplayable length reaching here means a
 * caller skipped its `isSupportedLength` gate, and silently inventing a
 * board would corrupt the game rather than surface the bug.
 */
export function maxGuessesFor(answerOrLength: string | number): number {
  const length = typeof answerOrLength === 'string' ? answerOrLength.length : answerOrLength;
  if (!isSupportedLength(length)) throw new Error(`unsupported word length: ${length}`);
  return length + 1;
}

/** The most rows any playable length can have (7 letters → 8 rows). */
export const MAX_ROWS: number = maxGuessesFor(Math.max(...SUPPORTED_LENGTHS));

/**
 * Base XP by guess number, extended to 8 rows (spec §10.1). Index is
 * `guessCount - 1`. The first six entries are exactly the original
 * 5-letter table, which stays exported from xp.ts as `WIN_XP`.
 */
export const BASE_XP_BY_GUESS: readonly number[] = [100, 80, 60, 50, 40, 30, 25, 20];

/** Difficulty multiplier by answer length (spec §10.1). */
export function lengthMultiplier(length: number): number {
  switch (length) {
    case 4: return 0.8;
    case 5: return 1;
    case 6: return 1.25;
    case 7: return 1.5;
    default: throw new Error(`unsupported word length: ${length}`);
  }
}
